/**
 * W3-M3 — iframe-bridge handshake state machine + outcome computation (Bundle 3 M3).
 *
 * Spec: 02_specs/14_m3_iframe_bridge_version_capability_negotiation_2026-05-31.md
 *   §2 (problem)
 *   §4 (user-visible behavior — happy/degrade/warn/block paths)
 *   §5.1 (handshake message families)
 *   §5.3 (compatibility matrix)
 *   §5.4 (capability registry)
 *   §6  (ABAC / HMAC + replay rejection)
 *   §7  (failure modes F-M3-01..08)
 *
 * State machine (idle → requesting → negotiated|errored):
 *
 *   idle
 *     │  startHandshake(request)
 *     ▼
 *   requesting ── 5s timeout ──► errored(TIMEOUT) → BLOCK
 *     │
 *     ▼
 *   ackReceived(ack) or errorReceived(err) or schemaInvalid
 *     │                              │             │
 *     ▼                              ▼             ▼
 *   negotiated(outcome)         errored(code)  errored(SCHEMA_INVALID)
 *
 * Replay (spec §6.4): `(request.id, iframe_mount_id)` is the idempotency tuple.
 * The store dedups within a single mount; replays return the prior outcome and
 * emit `bridge_handshake_replay`.
 *
 * Pure state model — no HTTP, no postMessage. Callers wire transport.
 */

import {
  CAPABILITY_REGISTRY_V2,
  CapabilityTag,
  SHELL_BRIDGE_VERSION,
  getCapability,
  hardRequiredCapabilities,
} from './capability-registry';
import {
  HandshakeBaselineOutcome,
  SemverParts,
  compareVersionOutcome,
  isValidSemver,
  parseSemver,
  versionBaselineOutcome,
} from './version';

export type HandshakeStateName = 'idle' | 'requesting' | 'negotiated' | 'errored';

export type HandshakeOutcome = 'ALLOW' | 'DEGRADE' | 'WARN' | 'BLOCK';

export type HandshakeErrorCode =
  | 'MAJOR_MISMATCH'
  | 'MISSING_REQUIRED_CAPABILITY'
  | 'HMAC_VERIFY_FAILED'
  | 'SCHEMA_INVALID'
  | 'TIMEOUT';

export interface HandshakeRequestPayload {
  /** Shell M3 implementation version (semver string). */
  shell_version: string;
  /** Capability tags shell advertises as supported. */
  shell_capabilities: ReadonlyArray<CapabilityTag>;
  /** Iframe-mount identifier — part of the replay dedup tuple. */
  iframe_mount_id: string;
  /** Per-handshake UUID. */
  request_id: string;
  /** HS256 signature over canonical payload (Q-AUTH-04). Stored for audit; verification
   *  is the recipient's responsibility. */
  hmac: string;
}

export interface HandshakeAckPayload {
  app_version: string;
  app_capabilities: ReadonlyArray<string>; // arbitrary strings; shell ignores unknown tags
  in_reply_to: string; // == request.request_id
  hmac: string;
}

export interface HandshakeErrorPayload {
  code: HandshakeErrorCode;
  detail: string;
  in_reply_to?: string;
  required_capabilities?: ReadonlyArray<CapabilityTag>;
}

export interface NegotiationResult {
  outcome: HandshakeOutcome;
  /** Capability tags negotiated as usable (intersection of shell + app + hard-required). */
  negotiated_capabilities: ReadonlyArray<CapabilityTag>;
  /** Capability tags the shell wanted but the app does not provide. */
  missing_capabilities: ReadonlyArray<CapabilityTag>;
  /** Captured for audit. */
  shell_version: string;
  app_version: string;
  /** When outcome = BLOCK + scout.audit.v1 missing → fallback path 'F5' per spec §4. */
  fallback_path?: string;
  /** Reason copy for banner / audit. */
  reason: string;
}

export interface HandshakeState {
  name: HandshakeStateName;
  request: HandshakeRequestPayload | null;
  ack: HandshakeAckPayload | null;
  result: NegotiationResult | null;
  error_code: HandshakeErrorCode | null;
  error_detail: string | null;
  at: number;
}

export interface HandshakeAudit {
  event:
    | 'bridge_handshake_request_sent'
    | 'bridge_handshake_ack_received'
    | 'bridge_handshake_error'
    | 'bridge_handshake_outcome_allow'
    | 'bridge_handshake_outcome_degrade'
    | 'bridge_handshake_outcome_warn'
    | 'bridge_handshake_outcome_block'
    | 'bridge_handshake_replay'
    | 'bridge_handshake_capability_violation';
  detail?: string;
  at: number;
}

export interface BridgeHandshakeStoreOptions {
  on_audit?: (a: HandshakeAudit) => void;
  clock?: () => number;
  /** Ack timeout (ms). Spec §7 F-M3-05 → TIMEOUT = 5s. */
  ack_timeout_ms?: number;
}

const INITIAL: HandshakeState = {
  name: 'idle',
  request: null,
  ack: null,
  result: null,
  error_code: null,
  error_detail: null,
  at: 0,
};

const DEFAULT_ACK_TIMEOUT_MS = 5_000;

const OUTCOME_TO_AUDIT: Record<HandshakeOutcome, HandshakeAudit['event']> = {
  ALLOW: 'bridge_handshake_outcome_allow',
  DEGRADE: 'bridge_handshake_outcome_degrade',
  WARN: 'bridge_handshake_outcome_warn',
  BLOCK: 'bridge_handshake_outcome_block',
};

export class BridgeHandshakeStore {
  private state: HandshakeState = { ...INITIAL };
  /** Dedup tuple keyed on `request_id::iframe_mount_id` → prior result. */
  private readonly replayMemo = new Map<string, NegotiationResult | HandshakeErrorCode>();
  private readonly on_audit: ((a: HandshakeAudit) => void) | undefined;
  private readonly clock: () => number;
  private readonly ack_timeout_ms: number;

  constructor(opts: BridgeHandshakeStoreOptions = {}) {
    this.on_audit = opts.on_audit;
    this.clock = opts.clock ?? Date.now;
    this.ack_timeout_ms = opts.ack_timeout_ms ?? DEFAULT_ACK_TIMEOUT_MS;
  }

  /** Read-only snapshot. */
  current(): Readonly<HandshakeState> {
    return this.state;
  }

  /** Test-only reset; cleared replay memo too. */
  reset(): void {
    this.state = { ...INITIAL };
    this.replayMemo.clear();
  }

  /** Move from idle → requesting and emit `bridge_handshake_request_sent`.
   *  Returns the replay key. */
  startHandshake(req: HandshakeRequestPayload): string {
    const key = this.replayKey(req);
    const prior = this.replayMemo.get(key);
    if (prior !== undefined) {
      // Replay — return prior outcome / error; do NOT re-emit request_sent.
      this.emit('bridge_handshake_replay', `replay of ${key}`);
      if (typeof prior === 'string') {
        // prior was an error code
        this.state = {
          name: 'errored',
          request: req,
          ack: null,
          result: null,
          error_code: prior,
          error_detail: 'replay of prior error',
          at: this.clock(),
        };
      } else {
        // prior was a successful negotiation outcome
        this.state = {
          name: 'negotiated',
          request: req,
          ack: null,
          result: prior,
          error_code: null,
          error_detail: null,
          at: this.clock(),
        };
      }
      return key;
    }

    if (!isValidSemver(req.shell_version)) {
      this.recordError(req, 'SCHEMA_INVALID', `shell_version not semver: ${req.shell_version}`);
      this.replayMemo.set(key, 'SCHEMA_INVALID');
      return key;
    }

    this.state = {
      name: 'requesting',
      request: req,
      ack: null,
      result: null,
      error_code: null,
      error_detail: null,
      at: this.clock(),
    };
    this.emit('bridge_handshake_request_sent');
    return key;
  }

  /** Move from requesting → negotiated. Computes the outcome via compat matrix +
   *  capability-intersection rules. Emits the appropriate outcome audit event. */
  receiveAck(ack: HandshakeAckPayload): NegotiationResult {
    if (this.state.name !== 'requesting' || this.state.request === null) {
      throw new HandshakeProtocolError(
        'INVALID_TRANSITION',
        `cannot receive ack from state=${this.state.name}`,
      );
    }
    if (!isValidSemver(ack.app_version)) {
      const err = this.recordError(
        this.state.request,
        'SCHEMA_INVALID',
        `app_version not semver: ${ack.app_version}`,
      );
      this.replayMemo.set(this.replayKey(this.state.request), 'SCHEMA_INVALID');
      // Synthesize a BLOCK result so callers always receive a result.
      return this.blockResult(this.state.request, ack, 'SCHEMA_INVALID', err);
    }
    if (ack.in_reply_to !== this.state.request.request_id) {
      const detail = `ack in_reply_to ${ack.in_reply_to} does not match request_id ${this.state.request.request_id}`;
      this.recordError(this.state.request, 'SCHEMA_INVALID', detail);
      this.replayMemo.set(this.replayKey(this.state.request), 'SCHEMA_INVALID');
      return this.blockResult(this.state.request, ack, 'SCHEMA_INVALID', detail);
    }

    this.emit('bridge_handshake_ack_received');

    const req = this.state.request;
    const result = computeNegotiation(req, ack);
    this.state = {
      name: 'negotiated',
      request: req,
      ack,
      result,
      error_code: null,
      error_detail: null,
      at: this.clock(),
    };
    this.replayMemo.set(this.replayKey(req), result);
    this.emit(OUTCOME_TO_AUDIT[result.outcome], result.reason);
    return result;
  }

  /** App or transport returned a structured error. Transitions to `errored`. */
  receiveError(err: HandshakeErrorPayload): void {
    if (this.state.request === null) return;
    this.recordError(this.state.request, err.code, err.detail);
    this.replayMemo.set(this.replayKey(this.state.request), err.code);
  }

  /** Caller invokes when their 5s ack timer fires. */
  reportTimeout(): NegotiationResult {
    if (this.state.request === null) {
      throw new HandshakeProtocolError('INVALID_TRANSITION', 'no request to time out');
    }
    const req = this.state.request;
    const detail = `no ack within ${this.ack_timeout_ms}ms`;
    this.recordError(req, 'TIMEOUT', detail);
    this.replayMemo.set(this.replayKey(req), 'TIMEOUT');
    return this.blockResult(req, null, 'TIMEOUT', detail);
  }

  /** Dispatcher gating: returns true only when the negotiated set permits this
   *  capability tag. False when not yet negotiated, when outcome != ALLOW/DEGRADE,
   *  or when the tag is not in the negotiated_capabilities set. */
  canSend(tag: CapabilityTag): boolean {
    if (this.state.name !== 'negotiated' || this.state.result === null) return false;
    const r = this.state.result;
    if (r.outcome === 'BLOCK') return false;
    return r.negotiated_capabilities.includes(tag);
  }

  /** Test helper. */
  ackTimeoutMs(): number {
    return this.ack_timeout_ms;
  }

  // --------------------------------------------------------- internals

  private replayKey(req: HandshakeRequestPayload): string {
    return `${req.request_id}::${req.iframe_mount_id}`;
  }

  private recordError(
    req: HandshakeRequestPayload,
    code: HandshakeErrorCode,
    detail: string,
  ): string {
    this.state = {
      name: 'errored',
      request: req,
      ack: null,
      result: null,
      error_code: code,
      error_detail: detail,
      at: this.clock(),
    };
    this.emit('bridge_handshake_error', `${code}: ${detail}`);
    return detail;
  }

  private blockResult(
    req: HandshakeRequestPayload,
    ack: HandshakeAckPayload | null,
    code: HandshakeErrorCode,
    reason: string,
  ): NegotiationResult {
    const result: NegotiationResult = {
      outcome: 'BLOCK',
      negotiated_capabilities: [],
      missing_capabilities: hardRequiredCapabilities(),
      shell_version: req.shell_version,
      app_version: ack?.app_version ?? 'unknown',
      fallback_path: 'F5',
      reason: `${code}: ${reason}`,
    };
    this.emit('bridge_handshake_outcome_block', result.reason);
    return result;
  }

  private emit(event: HandshakeAudit['event'], detail?: string): void {
    if (this.on_audit === undefined) return;
    // Best-effort: audit emission MUST NOT throw and MUST NOT block bridge
    // dispatch / state transitions. Matches the canonical sibling pattern at
    // `./audit.ts:78-92` (BridgeAuditEmitter.emit) — both bridge audit paths
    // treat observability as a non-blocking side effect so a misbehaving
    // audit sink can't disrupt the handshake state machine.
    // Closeout 47 line 152 explicitly named "audit-throw-tolerance" as a
    // T-M3-14 coverage target; this isolation closes that intent.
    // Plan 29 Option E. The exception is intentionally swallowed; production
    // observability surfaces these via the separate audit-emission channel
    // (Bundle 2 W2-L2 `bridge_audit` POST), not via this state-machine path.
    try {
      this.on_audit({ event, detail, at: this.clock() });
    } catch {
      // Swallow — see comment above. No re-throw, no state mutation.
    }
  }
}

// =============================================================================
// Pure helpers (exported for tests)
// =============================================================================

export class HandshakeProtocolError extends Error {
  constructor(public readonly code: 'INVALID_TRANSITION' | 'SCHEMA_INVALID', message: string) {
    super(message);
    this.name = 'HandshakeProtocolError';
  }
}

/**
 * Compute the negotiation result from a request + ack.
 *
 * Compat matrix (spec §5.3):
 *   major mismatch                  → BLOCK
 *   major equal + minor equal       → ALLOW (caps decide further; never DEGRADE)
 *   major equal + minor diff/patch  → DEGRADE / WARN per capability set
 *
 * Capability rules (spec §5.4 + §4):
 *   hard_required missing           → BLOCK + fallback_path = 'F5' if scout.audit.v1
 *   hard_with_workaround missing    → WARN (overrides DEGRADE)
 *   soft missing                    → DEGRADE
 *   all caps present                → ALLOW
 */
export function computeNegotiation(
  req: HandshakeRequestPayload,
  ack: HandshakeAckPayload,
): NegotiationResult {
  const shell = parseSemver(req.shell_version);
  const app = parseSemver(ack.app_version);
  if (shell === null || app === null) {
    return {
      outcome: 'BLOCK',
      negotiated_capabilities: [],
      missing_capabilities: hardRequiredCapabilities(),
      shell_version: req.shell_version,
      app_version: ack.app_version,
      fallback_path: 'F5',
      reason: 'SCHEMA_INVALID: version is not semver',
    };
  }

  const baseline = versionBaselineOutcome(shell, app);
  if (baseline === 'BLOCK') {
    return {
      outcome: 'BLOCK',
      negotiated_capabilities: [],
      missing_capabilities: hardRequiredCapabilities(),
      shell_version: req.shell_version,
      app_version: ack.app_version,
      fallback_path: 'F5',
      reason: `MAJOR_MISMATCH: shell ${shell.major}.x vs app ${app.major}.x`,
    };
  }

  return computeCapabilityOutcome(req, ack, shell, app, baseline);
}

function computeCapabilityOutcome(
  req: HandshakeRequestPayload,
  ack: HandshakeAckPayload,
  shell: SemverParts,
  app: SemverParts,
  baseline: HandshakeBaselineOutcome,
): NegotiationResult {
  const shellSet = new Set<CapabilityTag>(req.shell_capabilities);
  const appSet = new Set<string>(ack.app_capabilities);

  // Intersection = capabilities both sides declared.
  const negotiated: CapabilityTag[] = [];
  for (const tag of shellSet) {
    if (appSet.has(tag)) negotiated.push(tag);
  }
  // Missing = shell-declared tags the app does not provide.
  const missing: CapabilityTag[] = [];
  for (const tag of shellSet) {
    if (!appSet.has(tag)) missing.push(tag);
  }

  // Apply hardness rules — strictest wins.
  let outcome: HandshakeOutcome = baseline === 'DEGRADE' ? 'DEGRADE' : 'ALLOW';
  let reason = `version_outcome=${compareVersionOutcome(shell, app)}; caps_ok`;
  let fallback_path: string | undefined;

  const missingHardRequired: CapabilityTag[] = [];
  const missingHardWithWorkaround: CapabilityTag[] = [];
  const missingSoft: CapabilityTag[] = [];

  for (const tag of missing) {
    const def = getCapability(tag);
    if (def === null) continue; // unknown shell-declared cap — shouldn't happen, ignore
    if (def.hardness === 'hard_required') missingHardRequired.push(tag);
    else if (def.hardness === 'hard_with_workaround') missingHardWithWorkaround.push(tag);
    else missingSoft.push(tag);
  }

  if (missingHardRequired.length > 0) {
    outcome = 'BLOCK';
    fallback_path = missingHardRequired.includes('scout.audit.v1') ? 'F5' : 'F5';
    reason = `MISSING_REQUIRED_CAPABILITY: ${missingHardRequired.join(',')}`;
  } else if (missingHardWithWorkaround.length > 0) {
    outcome = 'WARN';
    reason = `WORKAROUND_REQUIRED: ${missingHardWithWorkaround.join(',')}`;
  } else if (missingSoft.length > 0 && outcome !== 'DEGRADE') {
    outcome = 'DEGRADE';
    reason = `SOFT_CAPABILITIES_MISSING: ${missingSoft.join(',')}`;
  } else if (missingSoft.length > 0) {
    // outcome was already DEGRADE from baseline; refine the reason
    reason = `DEGRADE (minor shell-newer; soft missing: ${missingSoft.join(',')})`;
  } else if (outcome === 'DEGRADE') {
    reason = `DEGRADE (minor shell-newer; all caps present)`;
  } else {
    reason = `ALLOW (version compat + all shell caps satisfied)`;
  }

  return {
    outcome,
    negotiated_capabilities: negotiated,
    missing_capabilities: missing,
    shell_version: req.shell_version,
    app_version: ack.app_version,
    fallback_path,
    reason,
  };
}

/** Build a canonical handshake request payload for the shell side, pulling
 *  shell version + capabilities from the canonical registry. Tests use this so
 *  request payloads stay in sync with the registry. */
export function buildShellHandshakeRequest(args: {
  iframe_mount_id: string;
  request_id: string;
  hmac: string;
}): HandshakeRequestPayload {
  return {
    shell_version: SHELL_BRIDGE_VERSION,
    shell_capabilities: CAPABILITY_REGISTRY_V2.map((d) => d.tag),
    iframe_mount_id: args.iframe_mount_id,
    request_id: args.request_id,
    hmac: args.hmac,
  };
}
